import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Navbar from '../../components/layout/Navbar';
import DataTable from '../../components/common/DataTable';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { getGroups, getMembers } from '../../api/superAdminApi';
import { FolderOpen, Users, Clock } from 'lucide-react';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';

const ViewGroups = () => {
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getGroups();
      setGroups(res.data?.data?.groups || []);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleView = async (group) => {
    setSelected(group);
    setLoadingMembers(true);
    try {
      const res = await getMembers();
      const allMembers = res.data?.data?.members || [];
      setGroupMembers(allMembers.filter(m => m.group_id === group.group_id));
    } catch {
      toast.error('Failed to load group members');
    } finally {
      setLoadingMembers(false);
    }
  };

  const filteredGroups = groups.filter(g => {
    const matchesFilter = filter === 'All' ? true : g.status?.toLowerCase() === filter.toLowerCase();
    const matchesSearch = g.group_name.toLowerCase().includes(search.toLowerCase()) || 
                          (g.creator?.name || '').toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const safeFormat = (dateStr, fmt = 'dd MMM yyyy') => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isValid(d) ? format(d, fmt) : '—';
  };

  const columns = [
    { header: 'Group Name',  accessorKey: 'group_name',
      cell: ({ row: { original: g } }) => <span className="font-semibold text-gray-900">{g.group_name}</span> },
    { header: 'Owner',       accessorKey: 'owner_name',
      cell: ({ row: { original: g } }) => g.creator?.name || '—' },
    { header: 'Amount',      accessorKey: 'amount',
      cell: ({ row: { original: g } }) => <span className="font-bold text-blue-700">₹{(g.amount||0).toLocaleString('en-IN')}</span> },
    { header: 'Duration',    accessorKey: 'duration_months',
      cell: ({ row: { original: g } }) => `${g.duration_months} mo` },
    { header: 'Members',     accessorKey: 'member_count',
      cell: ({ row: { original: g } }) => <span className="font-semibold text-purple-700">{g.member_count || 0}</span> },
    { header: 'Status',      accessorKey: 'status',
      cell: ({ row: { original: g } }) => <Badge status={g.status} /> },
    { header: 'Start Date',  accessorKey: 'start_date',
      cell: ({ row: { original: g } }) => (
        <span className="text-xs text-gray-500">{safeFormat(g.start_date)}</span>
      )},
    { header: 'View',        id: 'actions',
      cell: ({ row: { original: g } }) => (
        <button onClick={() => handleView(g)}
          className="text-xs text-blue-600 hover:text-blue-800 font-semibold underline underline-offset-2">
          Details
        </button>
      )},
  ];

  return (
    <Layout>
      <Navbar title="All Groups" subtitle={`${groups.length} total groups`} onRefresh={fetchGroups} loading={loading} />
      <div className="p-6">
        <div className="card">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-xl"><FolderOpen size={20} className="text-purple-600" /></div>
              <div>
                <h2 className="font-bold text-gray-900">Bhishi Groups</h2>
                <p className="text-xs text-gray-500">All groups across all owners</p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search groups or owners..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full sm:w-64"
              />
            </div>
          </div>
          
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {['All', 'Active', 'Pending', 'Completed'].map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  filter === tab 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <DataTable columns={columns} data={filteredGroups} loading={loading} emptyMessage="No groups found" />
        </div>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Group Details & Timeline">
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-2xl">
                <FolderOpen size={24} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg">{selected.group_name}</h3>
                <Badge status={selected.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Owner',      value: selected.creator?.name || '—' },
                { label: 'Amount',     value: `₹${(selected.amount||0).toLocaleString('en-IN')}/mo` },
                { label: 'Duration',   value: `${selected.duration_months} months` },
                { label: 'Members',    value: selected.member_count || 0 },
                { label: 'Start Date', value: safeFormat(selected.start_date) },
                { label: 'End Date',   value: safeFormat(selected.end_date) },
              ].map(item => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-0.5">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{item.value}</p>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock size={16} className="text-blue-600" />
                Group Timeline
              </h4>
              <div className="relative pl-4 space-y-4 border-l-2 border-gray-100 ml-2">
                <div className="relative">
                  <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-blue-500 ring-4 ring-white" />
                  <p className="text-sm font-bold text-gray-800">Group Created</p>
                  <p className="text-xs text-gray-500">{safeFormat(selected.createdAt, 'dd MMM yyyy hh:mm a')}</p>
                </div>
                {selected.start_date && (
                  <div className="relative">
                    <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-teal-500 ring-4 ring-white" />
                    <p className="text-sm font-bold text-gray-800">Cycle Started</p>
                    <p className="text-xs text-gray-500">{safeFormat(selected.start_date)}</p>
                  </div>
                )}
                {selected.status === 'completed' && selected.end_date && (
                  <div className="relative">
                    <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-green-500 ring-4 ring-white" />
                    <p className="text-sm font-bold text-gray-800">Group Completed</p>
                    <p className="text-xs text-gray-500">{safeFormat(selected.end_date)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Member List */}
            <div>
              <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Users size={16} className="text-teal-600" />
                Member List
              </h4>
              {loadingMembers ? (
                <p className="text-sm text-gray-500">Loading members...</p>
              ) : groupMembers.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-xl text-center border border-dashed border-gray-200">No members have joined yet.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {groupMembers.map((m, i) => (
                    <div key={m.member_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-xs">
                          {m.user?.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{m.user?.name}</p>
                          <p className="text-xs text-gray-500">Share #{m.share_number || (i+1)}</p>
                        </div>
                      </div>
                      <Badge status={m.is_active ? 'active' : 'inactive'} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default ViewGroups;
